import styled, { css } from 'styled-components';

import { ds } from '@/config/colors';
import { media } from '@/utils';

/*
 * Układ jednego ekranu zamówienia personelu (#374). Wygląd jak formularz oferty specjalnej: sekcje
 * FormSection bez kart, pola HeroUI z inputProps, stopka FormActions. Kolory z tokenów ds, bez gradientów.
 */

/** Szerokość treści formularza (przekazywana do FormPageLayout, żeby nagłówek trzymał tę samą krawędź). */
export const STAFF_ORDER_MAX_WIDTH = 1200;

/**
 * Od 834 px dwie kolumny: po lewej w kolejności czytania punkt, produkty i dane zamawiającego ze stopką,
 * po prawej przyklejony koszyk z sumą. Poniżej jedna kolumna, a koszyk to pasek przyklejony do dołu ekranu.
 */
export const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 32px;
  align-items: start;

  ${media.md} {
    grid-template-columns: minmax(0, 1fr) 300px;
    gap: 40px;
  }

  ${media.lg} {
    grid-template-columns: minmax(0, 1fr) 340px;
    gap: 56px;
  }
`;

export const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  min-width: 0;
`;

/*
 * Prawa kolumna z koszykiem przyklejona do góry. Telefon: `display: contents`, żeby koszyk był dzieckiem
 * całej siatki i jego pasek trzymał się dołu ekranu przez cały formularz (sticky nie wychodzi poza rodzica).
 */
export const Aside = styled.div`
  display: contents;

  ${media.md} {
    position: sticky;
    top: 16px;
    display: block;
  }
`;

/** Dwa pola obok siebie od 834 px (pokój + gość, telefon + e-mail), jak tytuł i etykieta w ofercie specjalnej. */
export const FieldRow = styled.div<{ $narrowFirst?: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 24px;

  ${media.md} {
    grid-template-columns: ${({ $narrowFirst }) => ($narrowFirst ? '160px minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))')};
  }
`;

/** Filtr kategorii i wyszukiwarka w jednym rzędzie; na telefonie pod sobą. */
export const CatalogFilters = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;

  ${media.tabletUp} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const NoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

/* ------------------------------------------------------------- komunikaty */

export const SectionError = styled.p`
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.45;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#fca5a5' : '#b91c1c')};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(239, 68, 68, 0.12)' : '#fef2f2')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(239, 68, 68, 0.35)' : '#fecaca')};
`;

export const SectionHint = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const TextButton = styled.button`
  align-self: flex-start;
  flex-shrink: 0;
  min-height: 40px;
  padding: 0 4px;
  border: 0;
  background: none;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${ds.accentInk};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

/* ------------------------------------------------------------ kafelki punktów */

export const OutletPickerWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const OutletGrid = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;

  ${media.mobile} {
    gap: 8px;
  }
`;

const outletBorder = (selected: boolean, mode: string): string => {
  if (selected) return ds.accentInk;
  return mode === 'dark' ? '#374151' : ds.borderDefault;
};

export const OutletTile = styled.button<{ $selected: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 72px;
  padding: 12px 40px 12px 12px;
  border-radius: 10px;
  border: 1px solid ${({ $selected, theme }) => outletBorder($selected, theme.mode)};
  background: ${({ $selected, theme }) => {
    if (theme.mode === 'dark') return $selected ? 'rgba(255, 255, 255, 0.06)' : theme.colors.background.card;
    return $selected ? ds.bgMuted : ds.bgSurface;
  }};
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  /* Wybrany kafelek: grubsza ramka bez przesuwania treści (obrys wewnętrzny zamiast 2 px border). */
  ${({ $selected }) => $selected && css`
    box-shadow: inset 0 0 0 1px ${ds.accentInk};
  `}

  &:hover {
    border-color: ${ds.accentInk};
  }

  &:focus-visible {
    outline: 2px solid ${ds.accentInk};
    outline-offset: 2px;
  }

  ${media.reducedMotion} {
    transition: none;
  }
`;

export const OutletThumb = styled.span<{ $small?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ $small }) => ($small ? 36 : 48)}px;
  height: ${({ $small }) => ($small ? 36 : 48)}px;
  border-radius: 8px;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : ds.bgMuted)};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const OutletTileText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const OutletName = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 15px;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

export const OutletCheck = styled.span`
  position: absolute;
  top: 50%;
  right: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-top: -10px;
  border-radius: 999px;
  background: ${ds.accentInk};
  color: #ffffff;
`;

/** Zwinięty wybór punktu: „Punkt: X · Zmień”, żeby produkty były wyżej. */
export const OutletBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 8px 8px 8px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : ds.borderDefault)};
  background: ${({ theme }) => (theme.mode === 'dark' ? theme.colors.background.card : ds.bgSurface)};

  ${TextButton} {
    align-self: center;
    margin-left: auto;
    padding: 0 8px;
  }
`;

export const OutletBarText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;
